import React, { createContext, useContext, ReactNode, useState } from 'react';
import ApiClient from '../../lib/api-client';
import { IChatDTO } from '@common-frontend-submodule/interfaces/chat/ichat.interface';
import { IGetChatsRequestDTO } from '@common-frontend-submodule/interfaces/chat/iget-chats-request.interface';
import { IApiResponseGenericDTO } from '@common-frontend-submodule/interfaces/common/iapi-response.interface';
import { ChatRoutes } from '@common-frontend-submodule/enums/routes/chat-routes.enum';
import { ChatMessageRoutes } from '@common-frontend-submodule/enums/routes/chat-message-routes.enum'
import { IPaginatedResultDTO } from '@common-frontend-submodule/interfaces/common/ipaginated-result.interface';
import { IChatMessageDTO } from '@common-frontend-submodule/interfaces/chat-message/ichat-message.interface';
import { IChatMessageCreationDTO } from '@common-frontend-submodule/interfaces/chat-message/ichat-message-creation.interface'
import { IApplicationDTO } from '@common-frontend-submodule/interfaces/application/iapplication.interface';
const apiClient = ApiClient.getInstance()

interface ChatContextProps {
  getById: (id: string) => Promise<IApiResponseGenericDTO<IChatDTO>>
  getAll: (model: IGetChatsRequestDTO) => Promise<IApiResponseGenericDTO<IPaginatedResultDTO<IChatDTO>>>
  getAllMessages: (chatId: string) => Promise<IApiResponseGenericDTO<IChatMessageDTO[]>>
  create: (model: IChatMessageCreationDTO) => Promise<IApiResponseGenericDTO<boolean>>
  createFile: (file: File, model: IChatMessageCreationDTO) => Promise<IApiResponseGenericDTO<IChatMessageDTO>>;
  deleteMessage: (chatMessageId: string) => Promise<IApiResponseGenericDTO<boolean>>;
  getApplications: (id: string) => Promise<IApiResponseGenericDTO<IApplicationDTO[]>>
  unreadMessages: {
    [chatId: string]: {
      count: number,
      user: {
        firstName: string
        id: string
        lastName: string
        telegramId: string
        userName: string
      }
    }
  }
  setUnreadMessages: React.Dispatch<React.SetStateAction<{
    [chatId: string]: {
      count: number,
      user: {
        firstName: string
        id: string
        lastName: string
        telegramId: string
        userName: string
      }
    }
  }>>
}

const ChatContext = createContext<ChatContextProps | undefined>(undefined);

export const ChatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [unreadMessages, setUnreadMessages] = useState<{
    [chatId: string]: {
      count: number,
      user: {
        firstName: string
        id: string
        lastName: string
        telegramId: string
        userName: string
      }
    }
  }>({});

  const getAll = async (model: IGetChatsRequestDTO): Promise<IApiResponseGenericDTO<IPaginatedResultDTO<IChatDTO>>> => {
    return await apiClient.postRequest<IGetChatsRequestDTO, IPaginatedResultDTO<IChatDTO>>(
      `${ChatRoutes.BasePrefix}/${ChatRoutes.List}`, model
    );
  };

  const getById = async (id: string): Promise<IApiResponseGenericDTO<IChatDTO>> => {
    return await apiClient.getRequest<IChatDTO>(`${ChatRoutes.BasePrefix}/${id}`);
  };

  const getAllMessages = async (chatId: string): Promise<IApiResponseGenericDTO<IChatMessageDTO[]>> => {
    return await apiClient.getRequest<IChatMessageDTO[]>(`${ChatMessageRoutes.BasePrefix}/all/${chatId}`);
  };

  const create = async (model: IChatMessageCreationDTO): Promise<IApiResponseGenericDTO<boolean>> => {
    return await apiClient.postRequest<IChatMessageCreationDTO, boolean>(`${ChatMessageRoutes.BasePrefix}`, model);
  };

  const createFile = async (file: File, model: IChatMessageCreationDTO): Promise<IApiResponseGenericDTO<IChatMessageDTO>> => {
    const formData = new FormData();

    formData.append('files', file);

    Object.entries(model).forEach(([key, value]) => {
      formData.append(key, value as string); // Преобразуем значение в строку
    });

    return await apiClient.postFormDataRequest<FormData, IChatMessageDTO>(
      ChatMessageRoutes.BasePrefix + '/file',
      formData,
    );
  };

  const deleteMessage = async (chatMessageId: string): Promise<IApiResponseGenericDTO<boolean>> => {
    return await apiClient.deleteRequest<null, boolean>(`${ChatMessageRoutes.BasePrefix}/${chatMessageId}`);
  };

  const getApplications = async (id: string): Promise<IApiResponseGenericDTO<IApplicationDTO[]>> => {
    return await apiClient.getRequest<IApplicationDTO[]>(`${ChatRoutes.BasePrefix}/applications/${id}`);
  };


  return (
    <ChatContext.Provider value={{ getById, getAll, getAllMessages, create, createFile, deleteMessage, getApplications, unreadMessages, setUnreadMessages }}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChatStore = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChatStore must be used within a ChatProvider');
  }
  return context;
};
